import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS, DOMAIN } from '../config/apiConfig';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const SEOContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
  color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-top: 30px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const BackButton = styled(Link)`
  padding: 10px 20px;
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--gradient-pink);
    transform: translateY(-2px);
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
`;

const SettingsSection = styled.div`
  background: var(--glass-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 25px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: var(--primary-color);
  border-bottom: 2px solid var(--gradient-pink);
  padding-bottom: 10px;
`;

const SectionDescription = styled.p`
  color: var(--secondary-color);
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
`;

const SettingItem = styled.div`
  margin-bottom: 25px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: var(--primary-color);
    font-size: 0.95rem;
  }
  
  .label-hint {
    font-size: 0.8rem;
    color: var(--secondary-color);
    font-weight: 400;
    margin-top: 4px;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 12px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--primary-color);
    font-size: 1rem;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: var(--gradient-pink);
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
    line-height: 1.6;
  }
  
  .char-count {
    font-size: 0.8rem;
    color: var(--secondary-color);
    margin-top: 5px;
    text-align: right;
  }
`;

const PreviewBox = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 15px;
  margin-top: 10px;
  
  .preview-title {
    font-size: 0.85rem;
    color: var(--secondary-color);
    margin-bottom: 8px;
  }
  
  .preview-content {
    color: var(--primary-color);
    
    .preview-url {
      color: #0f9d58;
      font-size: 0.9rem;
      margin-bottom: 5px;
    }
    
    .preview-heading {
      color: #1a73e8;
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: 5px;
      cursor: pointer;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    .preview-description {
      color: #5f6368;
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`;

const SocialPreviewContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SocialPreviewBox = styled.div`
  background: ${props => props.$bgColor || 'rgba(0, 0, 0, 0.4)'};
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 15px;
  border-left: 4px solid ${props => props.$borderColor || 'var(--gradient-pink)'};
  
  .preview-platform {
    font-size: 0.75rem;
    color: var(--secondary-color);
    margin-bottom: 10px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 1px;
  }
  
  .preview-info {
    font-size: 0.7rem;
    color: var(--secondary-color);
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const FacebookPreview = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .fb-image {
    width: 100%;
    height: 250px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.8rem;
    position: relative;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .no-image {
      text-align: center;
      opacity: 0.7;
    }
  }
  
  .fb-content {
    padding: 12px;
    color: #1c1e21;
    
    .fb-url {
      font-size: 0.75rem;
      color: #606770;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    
    .fb-title {
      font-size: 1rem;
      font-weight: 600;
      color: #1c1e21;
      margin-bottom: 4px;
      line-height: 1.3;
    }
    
    .fb-description {
      font-size: 0.875rem;
      color: #606770;
      line-height: 1.4;
    }
  }
`;

const TwitterPreview = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .tw-image {
    width: 100%;
    height: ${props => props.$largeImage ? '280px' : '120px'};
    background: linear-gradient(135deg, #1da1f2 0%, #0d8bd9 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.8rem;
    position: relative;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .no-image {
      text-align: center;
      opacity: 0.7;
    }
  }
  
  .tw-content {
    padding: 12px;
    color: #14171a;
    
    .tw-title {
      font-size: ${props => props.$largeImage ? '1rem' : '0.875rem'};
      font-weight: 600;
      color: #14171a;
      margin-bottom: 4px;
      line-height: 1.3;
    }
    
    .tw-description {
      font-size: 0.875rem;
      color: #657786;
      line-height: 1.4;
      margin-bottom: 4px;
    }
    
    .tw-url {
      font-size: 0.75rem;
      color: #657786;
    }
  }
`;

const SaveButton = styled.button`
  padding: 15px 40px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  background: var(--gradient-pink);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(46, 213, 115, 0.2);
  border: 1px solid #2ed573;
  color: #2ed573;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  background: rgba(255, 71, 87, 0.2);
  border: 1px solid #ff4757;
  color: #ff4757;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 50px;
  color: var(--secondary-color);
  font-size: 1.2rem;
`;

const ImageEditorContainer = styled.div`
  position: relative;
  margin-top: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
`;

const ImageEditorControls = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
  align-items: center;
`;

const ControlButton = styled.button`
  padding: 8px 15px;
  background: var(--gradient-pink);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  input[type="range"] {
    width: 150px;
  }
  
  span {
    color: var(--primary-color);
    font-size: 0.9rem;
    min-width: 60px;
  }
`;

const EditableImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  overflow: hidden;
  background: #000;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  
  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) translate(${props => props.$translateX}px, ${props => props.$translateY}px) scale(${props => props.$scale});
    transform-origin: center center;
    max-width: none;
    max-height: none;
    pointer-events: none;
  }
`;

const AdminSEOPage = () => {
  const [settings, setSettings] = useState({});
  const [seoSettings, setSeoSettings] = useState({
    seo_site_title: '',
    seo_site_description: '',
    seo_site_keywords: '',
    seo_home_title: '',
    seo_home_description: '',
    seo_home_keywords: '',
    seo_og_image: '',
    seo_twitter_card: 'summary_large_image',
    seo_robots: 'index, follow',
    seo_canonical_url: '',
    seo_author: '',
    seo_language: 'tr',
    seo_geo_region: '',
    seo_geo_placename: '',
    seo_geo_position: '',
    seo_revisit_after: '7 days',
    seo_distribution: 'global',
    seo_rating: 'general'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [imageTransform, setImageTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const handleImageMouseMove = React.useCallback((e) => {
    if (!isDragging || !seoSettings.seo_og_image) return;
    setImageTransform(prev => ({
      ...prev,
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    }));
  }, [isDragging, seoSettings.seo_og_image, dragStart]);

  const handleImageMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleImageMouseMove);
      document.addEventListener('mouseup', handleImageMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleImageMouseMove);
        document.removeEventListener('mouseup', handleImageMouseUp);
      };
    }
  }, [isDragging, handleImageMouseMove, handleImageMouseUp]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.GET_SETTINGS));
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        
        // SEO ayarlarını yükle
        const seoData = {};
        Object.keys(seoSettings).forEach(key => {
          seoData[key] = data[key] || seoSettings[key];
        });
        setSeoSettings(seoData);
      }
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      setMessage({ type: 'error', text: 'Ayarlar yüklenirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSeoSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await fetch(getApiUrl(API_ENDPOINTS.SAVE_SETTINGS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(seoSettings)
      });

      const result = await response.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: 'SEO ayarları başarıyla kaydedildi' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: result.message || 'Kaydetme sırasında hata oluştu' });
      }
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      setMessage({ type: 'error', text: 'Kaydetme sırasında hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  const getSiteTitle = () => {
    return settings.site_title || 'CineGlass';
  };

  const getPreviewTitle = () => {
    if (seoSettings.seo_home_title) {
      return seoSettings.seo_home_title.includes(getSiteTitle())
        ? seoSettings.seo_home_title
        : `${seoSettings.seo_home_title} - ${getSiteTitle()}`;
    }
    return `${getSiteTitle()} - Türkçe Dublaj ve Altyazılı Film ve Dizi İzle`;
  };

  const getPreviewDescription = () => {
    return seoSettings.seo_home_description || seoSettings.seo_site_description || 'Film ve dizi izleme platformu';
  };

  // Preview container'larına göre base scale hesapla
  const getBaseScale = (containerWidth, containerHeight) => {
    if (!imageSize.width || !imageSize.height) return 1;
    const scaleX = containerWidth / imageSize.width;
    const scaleY = containerHeight / imageSize.height;
    return Math.min(scaleX, scaleY); // Container'a tam sığdırmak için küçük olanı al
  };

  const handleImageMouseDown = (e) => {
    if (!seoSettings.seo_og_image) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imageTransform.x,
      y: e.clientY - imageTransform.y
    });
  };

  const handleZoom = React.useCallback((delta) => {
    setImageTransform(prev => {
      const newScale = Math.max(0.5, Math.min(3, prev.scale + delta));
      return {
        ...prev,
        scale: newScale
      };
    });
  }, []);

  const handleResetImage = () => {
    setImageTransform({ x: 0, y: 0, scale: 1 });
  };

  // Mouse wheel zoom
  const handleImageWheel = React.useCallback((e) => {
    if (!seoSettings.seo_og_image) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    handleZoom(delta);
  }, [seoSettings.seo_og_image, handleZoom]);

  // Wheel event listener'ı passive: false ile ekle
  useEffect(() => {
    const imageEditor = document.querySelector('[data-image-editor]');
    if (imageEditor && seoSettings.seo_og_image) {
      imageEditor.addEventListener('wheel', handleImageWheel, { passive: false });
      return () => {
        imageEditor.removeEventListener('wheel', handleImageWheel);
      };
    }
  }, [handleImageWheel, seoSettings.seo_og_image]);

  if (loading) {
    return (
      <SEOContainer>
        <LoadingText>Ayarlar yükleniyor...</LoadingText>
      </SEOContainer>
    );
  }

  return (
    <SEOContainer>
      <Header>
        <Title>SEO Ayarları</Title>
        <BackButton to="/admin">Panele Dön</BackButton>
      </Header>

      {message.text && (
        message.type === 'success' ? (
          <SuccessMessage>{message.text}</SuccessMessage>
        ) : (
          <ErrorMessage>{message.text}</ErrorMessage>
        )
      )}

      <SettingsGrid>
        {/* Genel SEO Ayarları */}
        <SettingsSection>
          <SectionTitle>Genel SEO Ayarları</SectionTitle>
          <SectionDescription>
            Sitenizin genel SEO ayarlarını buradan yönetebilirsiniz. Bu ayarlar tüm sayfalarda varsayılan olarak kullanılır.
          </SectionDescription>

          <SettingItem>
            <label>
              Site Başlığı
              <span className="label-hint">(Site ayarlarından çekilir: {getSiteTitle()})</span>
            </label>
            <input
              type="text"
              value={getSiteTitle()}
              disabled
              placeholder="Site ayarlarından yönetilir"
            />
          </SettingItem>

          <SettingItem>
            <label>
              SEO Site Başlığı
              <span className="label-hint">Varsayılan site başlığı (meta title için)</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_site_title}
              onChange={(e) => handleChange('seo_site_title', e.target.value)}
              placeholder={`${getSiteTitle()} - Türkçe Dublaj ve Altyazılı Film ve Dizi İzle`}
            />
            <div className="char-count">{seoSettings.seo_site_title.length} / 60 karakter (önerilen)</div>
          </SettingItem>

          <SettingItem>
            <label>
              SEO Site Açıklaması
              <span className="label-hint">Varsayılan site açıklaması (meta description için)</span>
            </label>
            <textarea
              value={seoSettings.seo_site_description}
              onChange={(e) => handleChange('seo_site_description', e.target.value)}
              placeholder="Sitenizin genel açıklaması. Arama motorlarında görünecek."
            />
            <div className="char-count">{seoSettings.seo_site_description.length} / 160 karakter (önerilen)</div>
          </SettingItem>

          <SettingItem>
            <label>
              SEO Site Anahtar Kelimeleri
              <span className="label-hint">Virgülle ayrılmış anahtar kelimeler</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_site_keywords}
              onChange={(e) => handleChange('seo_site_keywords', e.target.value)}
              placeholder="film izle, dizi izle, türkçe dublaj, türkçe altyazı"
            />
          </SettingItem>

          <SettingItem>
            <label>
              Yazar
              <span className="label-hint">Site yazarı veya şirket adı</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_author}
              onChange={(e) => handleChange('seo_author', e.target.value)}
              placeholder={getSiteTitle()}
            />
          </SettingItem>

          <SettingItem>
            <label>
              Dil
              <span className="label-hint">Site dili (ISO 639-1 kodu)</span>
            </label>
            <select
              value={seoSettings.seo_language}
              onChange={(e) => handleChange('seo_language', e.target.value)}
            >
              <option value="tr">Türkçe (tr)</option>
              <option value="en">İngilizce (en)</option>
            </select>
          </SettingItem>
        </SettingsSection>

        {/* Ana Sayfa SEO Ayarları */}
        <SettingsSection>
          <SectionTitle>Ana Sayfa SEO Ayarları</SectionTitle>
          <SectionDescription>
            Ana sayfa için özel SEO ayarları. Bu ayarlar sadece ana sayfada kullanılır.
          </SectionDescription>

          <SettingItem>
            <label>
              Ana Sayfa Başlığı
              <span className="label-hint">Ana sayfa için özel başlık</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_home_title}
              onChange={(e) => handleChange('seo_home_title', e.target.value)}
              placeholder={`${getSiteTitle()} - Türkçe Dublaj ve Altyazılı Film ve Dizi İzle`}
            />
            <div className="char-count">{seoSettings.seo_home_title.length} / 60 karakter (önerilen)</div>
            <PreviewBox>
              <div className="preview-title">Google Arama Önizlemesi:</div>
              <div className="preview-content">
                <div className="preview-url">{DOMAIN}</div>
                <div className="preview-heading">{getPreviewTitle()}</div>
                <div className="preview-description">{getPreviewDescription()}</div>
              </div>
            </PreviewBox>
          </SettingItem>

          <SettingItem>
            <label>
              Ana Sayfa Açıklaması
              <span className="label-hint">Ana sayfa için özel açıklama</span>
            </label>
            <textarea
              value={seoSettings.seo_home_description}
              onChange={(e) => handleChange('seo_home_description', e.target.value)}
              placeholder="Ana sayfa açıklaması. Breaking Bad Türkçe izle, Game of Thrones Türkçe izle gibi anahtar kelimeler ekleyin."
            />
            <div className="char-count">{seoSettings.seo_home_description.length} / 160 karakter (önerilen)</div>
          </SettingItem>

          <SettingItem>
            <label>
              Ana Sayfa Anahtar Kelimeleri
              <span className="label-hint">Ana sayfa için özel anahtar kelimeler</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_home_keywords}
              onChange={(e) => handleChange('seo_home_keywords', e.target.value)}
              placeholder="breaking bad türkçe izle, game of thrones türkçe izle, film izle, dizi izle"
            />
          </SettingItem>
        </SettingsSection>

        {/* Sosyal Medya SEO */}
        <SettingsSection>
          <SectionTitle>Sosyal Medya ve Open Graph</SectionTitle>
          <SectionDescription>
            Facebook, Twitter, LinkedIn, WhatsApp ve diğer sosyal medya platformları için Open Graph ayarları. 
            Bu ayarlar sitenizdeki içerikler paylaşıldığında nasıl görüneceğini belirler.
          </SectionDescription>

          <SettingItem>
            <label>
              Open Graph Görseli
              <span className="label-hint">Sosyal medyada paylaşımda görünecek görsel URL'i (Önerilen: 1200x630px)</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_og_image}
              onChange={(e) => {
                handleChange('seo_og_image', e.target.value);
                setImageTransform({ x: 0, y: 0, scale: 1 }); // Reset transform when image changes
              }}
              placeholder={`${DOMAIN}/og-image.jpg`}
            />
            <div className="char-count">
              {seoSettings.seo_og_image ? '✓ Görsel URL girildi' : '⚠ Görsel URL gerekli'}
            </div>
            
            {seoSettings.seo_og_image && (
              <ImageEditorContainer>
                <ImageEditorControls>
                  <ControlButton onClick={() => handleZoom(-0.1)}>-</ControlButton>
                  <ZoomControl>
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={imageTransform.scale}
                      onChange={(e) => setImageTransform({ ...imageTransform, scale: parseFloat(e.target.value) })}
                    />
                    <span>{(imageTransform.scale * 100).toFixed(0)}%</span>
                  </ZoomControl>
                  <ControlButton onClick={() => handleZoom(0.1)}>+</ControlButton>
                  <ControlButton onClick={handleResetImage}>Sıfırla</ControlButton>
                </ImageEditorControls>
                <EditableImageContainer
                  data-image-editor
                  $translateX={imageTransform.x}
                  $translateY={imageTransform.y}
                  $scale={imageTransform.scale}
                  $isDragging={isDragging}
                  onMouseDown={handleImageMouseDown}
                >
                  <img 
                    src={seoSettings.seo_og_image} 
                    alt="OG Image Editor"
                    draggable={false}
                    onLoad={(e) => {
                      setImageSize({
                        width: e.target.naturalWidth,
                        height: e.target.naturalHeight
                      });
                    }}
                  />
                </EditableImageContainer>
                <div style={{ padding: '10px', fontSize: '0.8rem', color: 'var(--secondary-color)', textAlign: 'center' }}>
                  💡 Görseli sürükleyerek kaydırabilir, mouse tekerleği veya +/- butonları ile yakınlaştırıp uzaklaştırabilirsiniz
                </div>
              </ImageEditorContainer>
            )}
          </SettingItem>

          <SettingItem>
            <label>
              Twitter Card Tipi
              <span className="label-hint">Twitter'da görünecek kart tipi</span>
            </label>
            <select
              value={seoSettings.seo_twitter_card}
              onChange={(e) => handleChange('seo_twitter_card', e.target.value)}
            >
              <option value="summary">Özet (summary) - Küçük görsel</option>
              <option value="summary_large_image">Büyük Görüntülü Özet (summary_large_image) - Büyük görsel</option>
            </select>
            <div className="char-count">
              {seoSettings.seo_twitter_card === 'summary_large_image' 
                ? '📱 Büyük görsel kartı kullanılacak (önerilen)' 
                : '📱 Küçük görsel kartı kullanılacak'}
            </div>
          </SettingItem>

          {/* Sosyal Medya Önizlemeleri */}
          <SocialPreviewContainer>
            <SocialPreviewBox $bgColor="rgba(24, 119, 242, 0.1)" $borderColor="#1877f2">
              <div className="preview-platform">Facebook / Open Graph</div>
              <FacebookPreview>
                <div className="fb-image" style={{ overflow: 'hidden', position: 'relative' }}>
                  {seoSettings.seo_og_image ? (
                    <img 
                      src={seoSettings.seo_og_image} 
                      alt="OG Preview" 
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: `${imageSize.width || 1200}px`,
                        height: `${imageSize.height || 630}px`,
                        transform: `translate(-50%, -50%) translate(${imageTransform.x}px, ${imageTransform.y}px) scale(${getBaseScale(504, 250) * imageTransform.scale})`,
                        transformOrigin: 'center center'
                      }}
                      onLoad={(e) => {
                        if (!imageSize.width || !imageSize.height) {
                          setImageSize({
                            width: e.target.naturalWidth,
                            height: e.target.naturalHeight
                          });
                        }
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }} 
                    />
                  ) : null}
                  <div className="no-image" style={{ display: seoSettings.seo_og_image ? 'none' : 'block' }}>
                    📷 Görsel yok<br />
                    <span style={{ fontSize: '0.7rem' }}>Open Graph görseli ekleyin</span>
                  </div>
                </div>
                <div className="fb-content">
                  <div className="fb-url">{DOMAIN.replace(/^https?:\/\//, '')}</div>
                  <div className="fb-title">{getPreviewTitle()}</div>
                  <div className="fb-description">{getPreviewDescription()}</div>
                </div>
              </FacebookPreview>
              <div className="preview-info">
                <strong>Kullanıldığı yerler:</strong> Facebook, LinkedIn, WhatsApp, Discord, Slack, Telegram
                <br />
                <strong>Görsel boyutu:</strong> 1200x630px (önerilen)
              </div>
            </SocialPreviewBox>

            <SocialPreviewBox $bgColor="rgba(29, 161, 242, 0.1)" $borderColor="#1da1f2">
              <div className="preview-platform">Twitter Card</div>
              <TwitterPreview $largeImage={seoSettings.seo_twitter_card === 'summary_large_image'}>
                {seoSettings.seo_twitter_card === 'summary_large_image' && (
                  <div className="tw-image" style={{ overflow: 'hidden', position: 'relative' }}>
                    {seoSettings.seo_og_image ? (
                      <img 
                        src={seoSettings.seo_og_image} 
                        alt="Twitter Preview" 
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: `${imageSize.width || 1200}px`,
                          height: `${imageSize.height || 630}px`,
                          transform: `translate(-50%, -50%) translate(${imageTransform.x}px, ${imageTransform.y}px) scale(${getBaseScale(504, 280) * imageTransform.scale})`,
                          transformOrigin: 'center center'
                        }}
                        onLoad={(e) => {
                          if (!imageSize.width || !imageSize.height) {
                            setImageSize({
                              width: e.target.naturalWidth,
                              height: e.target.naturalHeight
                            });
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }} 
                      />
                    ) : null}
                    <div className="no-image" style={{ display: seoSettings.seo_og_image ? 'none' : 'block' }}>
                      📷 Görsel yok
                    </div>
                  </div>
                )}
                <div className="tw-content">
                  <div className="tw-title">{getPreviewTitle()}</div>
                  <div className="tw-description">{getPreviewDescription()}</div>
                  <div className="tw-url">{DOMAIN.replace(/^https?:\/\//, '')}</div>
                </div>
                {seoSettings.seo_twitter_card === 'summary' && (
                  <div className="tw-image" style={{ height: '120px', marginTop: '12px', overflow: 'hidden', position: 'relative' }}>
                    {seoSettings.seo_og_image ? (
                      <img 
                        src={seoSettings.seo_og_image} 
                        alt="Twitter Preview" 
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: `${imageSize.width || 1200}px`,
                          height: `${imageSize.height || 630}px`,
                          transform: `translate(-50%, -50%) translate(${imageTransform.x}px, ${imageTransform.y}px) scale(${getBaseScale(504, 120) * imageTransform.scale})`,
                          transformOrigin: 'center center'
                        }}
                        onLoad={(e) => {
                          if (!imageSize.width || !imageSize.height) {
                            setImageSize({
                              width: e.target.naturalWidth,
                              height: e.target.naturalHeight
                            });
                          }
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'block';
                        }} 
                      />
                    ) : null}
                    <div className="no-image" style={{ display: seoSettings.seo_og_image ? 'none' : 'block' }}>
                      📷 Küçük görsel
                    </div>
                  </div>
                )}
              </TwitterPreview>
              <div className="preview-info">
                <strong>Kullanıldığı yerler:</strong> Twitter, X (eski Twitter)
                <br />
                <strong>Kart tipi:</strong> {seoSettings.seo_twitter_card === 'summary_large_image' ? 'Büyük görsel (önerilen)' : 'Küçük görsel'}
                <br />
                <strong>Görsel boyutu:</strong> {seoSettings.seo_twitter_card === 'summary_large_image' ? '1200x675px' : '120x120px'}
              </div>
            </SocialPreviewBox>
          </SocialPreviewContainer>
        </SettingsSection>

        {/* Teknik SEO */}
        <SettingsSection>
          <SectionTitle>Teknik SEO Ayarları</SectionTitle>
          <SectionDescription>
            Arama motorları için teknik SEO ayarları.
          </SectionDescription>

          <SettingItem>
            <label>
              Robots Meta Tag
              <span className="label-hint">Arama motorlarına yönelik talimatlar</span>
            </label>
            <select
              value={seoSettings.seo_robots}
              onChange={(e) => handleChange('seo_robots', e.target.value)}
            >
              <option value="index, follow">İndeksle ve Takip Et (index, follow)</option>
              <option value="index, nofollow">İndeksle ama Takip Etme (index, nofollow)</option>
              <option value="noindex, follow">İndeksleme ama Takip Et (noindex, follow)</option>
              <option value="noindex, nofollow">İndeksleme ve Takip Etme (noindex, nofollow)</option>
            </select>
          </SettingItem>

          <SettingItem>
            <label>
              Canonical URL
              <span className="label-hint">Varsayılan canonical URL (boş bırakılırsa otomatik)</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_canonical_url}
              onChange={(e) => handleChange('seo_canonical_url', e.target.value)}
              placeholder={DOMAIN}
            />
          </SettingItem>

          <SettingItem>
            <label>
              Yeniden Ziyaret Süresi
              <span className="label-hint">Arama motorlarına ne sıklıkla tekrar kontrol edileceğini söyle</span>
            </label>
            <select
              value={seoSettings.seo_revisit_after}
              onChange={(e) => handleChange('seo_revisit_after', e.target.value)}
            >
              <option value="1 day">1 Gün</option>
              <option value="3 days">3 Gün</option>
              <option value="7 days">7 Gün</option>
              <option value="14 days">14 Gün</option>
              <option value="30 days">30 Gün</option>
            </select>
          </SettingItem>

          <SettingItem>
            <label>
              İçerik Dağıtımı
              <span className="label-hint">İçeriğin dağıtım kapsamı</span>
            </label>
            <select
              value={seoSettings.seo_distribution}
              onChange={(e) => handleChange('seo_distribution', e.target.value)}
            >
              <option value="global">Global</option>
              <option value="local">Yerel</option>
            </select>
          </SettingItem>

          <SettingItem>
            <label>
              İçerik Derecelendirmesi
              <span className="label-hint">İçerik yaş sınırı</span>
            </label>
            <select
              value={seoSettings.seo_rating}
              onChange={(e) => handleChange('seo_rating', e.target.value)}
            >
              <option value="general">Genel İzleyici (general)</option>
              <option value="mature">Olgun İzleyici (mature)</option>
            </select>
          </SettingItem>
        </SettingsSection>

        {/* Coğrafi SEO */}
        <SettingsSection>
          <SectionTitle>Coğrafi SEO Ayarları</SectionTitle>
          <SectionDescription>
            Coğrafi konum bazlı SEO ayarları (opsiyonel).
          </SectionDescription>

          <SettingItem>
            <label>
              Coğrafi Bölge
              <span className="label-hint">ISO 3166-1 alpha-2 ülke kodu (örn: TR, US)</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_geo_region}
              onChange={(e) => handleChange('seo_geo_region', e.target.value)}
              placeholder="TR"
              maxLength="2"
            />
          </SettingItem>

          <SettingItem>
            <label>
              Coğrafi Yer Adı
              <span className="label-hint">Şehir veya bölge adı</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_geo_placename}
              onChange={(e) => handleChange('seo_geo_placename', e.target.value)}
              placeholder="İstanbul"
            />
          </SettingItem>

          <SettingItem>
            <label>
              Coğrafi Konum
              <span className="label-hint">Enlem ve boylam (örn: 41.0082;28.9784)</span>
            </label>
            <input
              type="text"
              value={seoSettings.seo_geo_position}
              onChange={(e) => handleChange('seo_geo_position', e.target.value)}
              placeholder="41.0082;28.9784"
            />
          </SettingItem>
        </SettingsSection>
      </SettingsGrid>

      <SaveButton onClick={handleSave} disabled={saving}>
        {saving ? 'Kaydediliyor...' : 'SEO Ayarlarını Kaydet'}
      </SaveButton>
    </SEOContainer>
  );
};

export default AdminSEOPage;

